/* eslint-disable jsx-a11y/no-redundant-roles -- role='list' / 'listitem' are the design's: Safari drops list semantics from a list styled with list-style: none */
import classNames from 'classnames';
import Link from 'next/link';
import { Trans, useTranslation } from 'next-i18next';
import { useEffect, useRef, useState } from 'react';

import { PauseChip } from '../components/pause-chip';
import { Picture } from '../components/picture';
import { CONTAINER_CLASS, EYEBROW_CLASS, GOLD_EM_CLASS, SECTION_CLASS, SECTION_HEAD_CENTER_CLASS, SECTION_LEAD_CENTER_CLASS, SECTION_TITLE_CLASS, SectionBackground } from '../components/ui';
import type { GameCover } from '../constants';
import { GAME_COVERS, GAME_COVERS_LANE_B, V4A_NAMESPACE } from '../constants';
import { useReducedMotion } from '../hooks/use-page-motion';
import page from '../page.module.css';

const COVER_CLASS = 'flex-none overflow-hidden rounded-[var(--r-md)] border border-solid border-[color:var(--rim-2)] bg-[color:var(--plate-2)] [box-shadow:var(--shadow-sm)] forced-colors:border-[color:CanvasText]';
const TRACK_CLASS = 'marquee__track m-0 flex flex-none list-none gap-[var(--mq-gap)] p-0';
const MAX_COPIES = 10;

interface LaneProps {
  covers: readonly GameCover[];
  copies: number;
  lane: 'a' | 'b';
}

/* THE DISJOINT-LANES CONTRACT: lane B carries the same covers rotated by nine, on the same pitch, speed and
   direction, so the two lanes never show a title twice. The first copy of lane A is the only one a screen
   reader meets; every other copy is aria-hidden with empty alts. */
function Lane({ covers, copies, lane }: LaneProps) {
  return (
    <div className={`marquee__row marquee__row--${lane} flex w-max`}>
      {Array.from({ length: copies }, (_copy, copyIndex) => (
        <ul key={copyIndex} className={classNames(TRACK_CLASS, page['v4a-game-track'])} role='list' aria-hidden={lane === 'b' || copyIndex > 0 ? 'true' : undefined}>
          {covers.map((cover) => (
            <li key={cover.file} className={COVER_CLASS}>
              <Picture name={cover.file} alt={lane === 'a' && copyIndex === 0 ? cover.title : ''} width={cover.width} height={cover.height} loading='lazy' className='block h-[var(--mq-h)] w-[var(--mq-cover-w)] object-cover object-[50%_50%]' />
            </li>
          ))}
        </ul>
      ))}
    </div>
  );
}

/** "Reinos compatíveis": two lanes of game covers sliding past (pausable; still under reduced motion). */
export function Games() {
  const { t } = useTranslation(V4A_NAMESPACE);
  const reduced = useReducedMotion();
  const marqueeRef = useRef<HTMLDivElement>(null);
  const [copies, setCopies] = useState(3);
  const [paused, setPaused] = useState(false);

  // Three copies are baked in; this tops them up for very wide screens so the strip always stays covered.
  useEffect(() => {
    const marquee = marqueeRef.current;
    if (!marquee) return undefined;
    let timer = 0;
    const fit = () => {
      const track = marquee.querySelector('.marquee__track');
      const width = track?.getBoundingClientRect().width ?? 0;
      if (!width) return;
      const needed = Math.max(2, Math.ceil(marquee.clientWidth / width) + 1);
      setCopies((current) => Math.min(MAX_COPIES, Math.max(current, needed)));
    };
    fit();
    document.fonts?.ready.then(fit).catch(() => undefined);
    const onResize = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(fit, 180);
    };
    window.addEventListener('resize', onResize);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  const isPaused = reduced || paused;

  return (
    <section className={`${SECTION_CLASS} sec--games`} id='reinos' aria-labelledby='games-title'>
      <SectionBackground />
      <div className={CONTAINER_CLASS}>
        <div className={SECTION_HEAD_CENTER_CLASS} data-reveal=''>
          <p className={`${EYEBROW_CLASS} min-w-0`}>{t('games.eyebrow')}</p>
          <h2 className={`${SECTION_TITLE_CLASS} min-w-0`} id='games-title'>
            <Trans t={t} i18nKey='games.title' components={{ em: <em className={GOLD_EM_CLASS} /> }} />
          </h2>
          <p className={SECTION_LEAD_CENTER_CLASS}>{t('games.lead')}</p>
        </div>
        <div className='mobar mt-[clamp(1.25rem,_1rem_+_1.5vw,_2rem)] flex flex-wrap items-center justify-between gap-4 border-t border-solid border-t-[color:var(--rim)] pt-4' data-reveal=''>
          <p className='mobar__k m-0 inline-flex min-w-0 flex-[1_1_12rem] items-center gap-2 font-mono text-[length:var(--fs-2xs)] uppercase tracking-[var(--ls-eyebrow)] text-[color:var(--fg-faint-aa)]'>
            <i className='size-[6px] flex-none rounded-[50%] bg-[color:var(--gold)] [box-shadow:0_0_10px_rgb(var(--gold-rgb)_/_.7)]' aria-hidden='true' />
            {t('games.catalog')}
            <span className='mobar__x'> · {t('games.catalog_note')}</span>
          </p>
          <PauseChip paused={isPaused} onToggle={() => setPaused((previous) => !previous)} labelPause={t('games.pause')} labelResume={t('games.resume')} hidden={reduced} iconSize={16} controls='games-marquee' />
        </div>
      </div>

      {/* The rows remount when the copy count changes, so every track restarts its slide in step. */}
      <div ref={marqueeRef} className={classNames('marquee mx-auto mt-5 grid max-w-[var(--mq-w)] items-center gap-3 overflow-hidden [--mq-cover-w:calc(var(--mq-h)_*_.75)]', isPaused && 'is-paused')} id='games-marquee' data-marquee=''>
        <Lane key={`a-${copies}`} covers={GAME_COVERS} copies={copies} lane='a' />
        <Lane key={`b-${copies}`} covers={GAME_COVERS_LANE_B} copies={copies} lane='b' />
      </div>

      <div className={CONTAINER_CLASS}>
        <p className='games__foot mt-6 text-center'>
          <Link className='linkish inline-flex items-center gap-[.4rem] text-[length:var(--fs-sm)] font-semibold uppercase tracking-[var(--ls-caps)]' href='/games' target='_blank' rel='noopener'>
            {t('games.all_link')}
            <span className='sr-only'> {t('a11y.new_tab')}</span>{' '}
            <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true' focusable='false'>
              <path d='M7 17 17 7' />
              <path d='M9 7h8v8' />
            </svg>
          </Link>
        </p>
      </div>
    </section>
  );
}
